// SideBar.js
import React, { useEffect, useState, useRef } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { NavDropdown } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'
import { useTranslation } from 'react-i18next'

function SideBar() {
	const [isOpen, setIsOpen] = useState(false)
	const sidebarRef = useRef(null)
	const { t } = useTranslation()

	const userLogin = useSelector((state) => state.userLogin)
	const { userInfo } = userLogin

	const navigate = useNavigate()
	const location = useLocation()

	// Close sidebar on route change
	useEffect(() => {
		setIsOpen(false)
	}, [location.pathname])

	useEffect(() => {
		if (!userInfo) {
			navigate('/')
		}
	}, [navigate, userInfo])

	// When sidebar opens, focus the currently active link so tab continues from there
	useEffect(() => {
		if (isOpen && sidebarRef.current) {
			const activeLink = sidebarRef.current.querySelector('a.sidebar-active-link')
			const firstLink = sidebarRef.current.querySelector('a.side-nav-link')
			const target = activeLink || firstLink
			if (target) {
				setTimeout(() => target.focus(), 50)
			}
		}
	}, [isOpen])

	const navLinkClass = ({ isActive }) =>
		`side-nav-link${isActive ? ' sidebar-active-link' : ''}`

	const tabIndex = isOpen ? 0 : -1

	// Check if any loyalty route is active for the dropdown highlight
	const isLoyaltyActive = [
		'/customer-loyalty/',
		'/validated-rewards/',
		'/loyalty-settings/',
	].includes(location.pathname)

	return (
		<>
			{/* Hamburger Button */}
			<button
				className="sidebar-hamburger"
				onClick={() => setIsOpen((prev) => !prev)}
				aria-label="Toggle sidebar"
				style={{ zIndex: 1070 }}
			>
				<span className={`hamburger-icon ${isOpen ? 'open' : ''}`}>
					<span></span>
					<span></span>
					<span></span>
				</span>
			</button>

			{/* Overlay */}
			{isOpen && (
				<div
					className="sidebar-overlay"
					onClick={() => setIsOpen(false)}
					style={{ zIndex: 1030 }}
				/>
			)}

			{/* Sidebar Panel */}
			<div
				ref={sidebarRef}
				className={`leftside-menu ${isOpen ? 'sidebar-expanded' : 'sidebar-hidden'}`}
				style={{ zIndex: 1050 }}
			>
				<div className="h-100" id="leftside-menu-container" data-simplebar>
					<ul className="side-nav">
						<li className="side-nav-title">{t('sidebar.navigation')}</li>

						{/* Static nav items */}
						{[
							{ to: '/homepage', label: t('sidebar.home'), end: true, icon: 'ri-home-3-line' },
							{ to: '/customers', label: t('sidebar.customers'), icon: 'ri-team-line' },
							{ to: '/square-merchants/', label: t('sidebar.squareMerchants'), icon: 'uil-calender' },
							{ to: '/billing/', label: t('sidebar.billing'), icon: 'ri-file-list-3-line' },
							{ to: '/referral/', label: t('sidebar.referral'), icon: 'ri-links-line' },
						].map(({ to, label, end, icon }) => (
							<li key={to} className="side-nav-item">
								<NavLink
									to={to}
									end={end}
									className={navLinkClass}
									tabIndex={tabIndex}
								>
									<i className={icon}></i>
									<span>{label}</span>
								</NavLink>
							</li>
						))}

						{/* Loyalty Program Dropdown */}
						<li className="side-nav-item">
							<span
								className={`side-nav-link${isLoyaltyActive ? ' sidebar-active-link' : ''}`}
								style={{ display: 'flex', alignItems: 'center' }}
							>
								<i className="uil-gift"></i>
								<NavDropdown title={t('sidebar.loyaltyProgram')} id="LoyaltyDropdown">
									<LinkContainer to="/customer-loyalty/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.customerLoyalty')}</NavDropdown.Item>
									</LinkContainer>
									<LinkContainer to="/validated-rewards/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.pendingRewards')}</NavDropdown.Item>
									</LinkContainer>
									<LinkContainer to="/loyalty-settings/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.loyaltySettings')}</NavDropdown.Item>
									</LinkContainer>
								</NavDropdown>
							</span>
						</li>

						{/* Analytics Dropdown */}
						<li className="side-nav-item">
							<span className="side-nav-link" style={{ display: 'flex', alignItems: 'center' }}>
								<i className="uil-chart-line"></i>
								<NavDropdown title={t('sidebar.analyticsInsights')} id="AnalyticsDropdown">
									{userInfo && userInfo.is_admin && (
										<LinkContainer to="/NPS/">
											<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.netPromoterScore')}</NavDropdown.Item>
										</LinkContainer>
									)}
									<LinkContainer to="/customer-churn-rate-graph/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.churnRate')}</NavDropdown.Item>
									</LinkContainer>
									<LinkContainer to="/increased-margin-chart/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.increasedMarginChart')}</NavDropdown.Item>
									</LinkContainer>
									{userInfo && userInfo.is_admin ? (
										<LinkContainer to="/repeat-purchase-rate-graph/">
											<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.repeatPurchase')}</NavDropdown.Item>
										</LinkContainer>
									) : (
										<LinkContainer to="/revenue-graph/">
											<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.revenueGraph')}</NavDropdown.Item>
										</LinkContainer>
									)}
								</NavDropdown>
							</span>
						</li>

						{/* Transactions and purchase Dropdown */}
						<li className="side-nav-item">
							<span className="side-nav-link" style={{ display: 'flex', alignItems: 'center' }}>
								<i className="ri-money-cny-box-line"></i>
								<NavDropdown title={t('sidebar.transactionsPurchases')} id="TransactionsPurchaseDropdown">
									{userInfo && userInfo.is_admin && (
										<LinkContainer to="/purchase-history/">
											<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.purchaseHistory')}</NavDropdown.Item>
										</LinkContainer>
									)}
									<LinkContainer to="/receipts-history/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.receiptsHistory')}</NavDropdown.Item>
									</LinkContainer>
									<LinkContainer to="/items-sold-details/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.itemsOverview')}</NavDropdown.Item>
									</LinkContainer>
								</NavDropdown>
							</span>
						</li>

						{/* Users Dropdown (admin only) */}
						{userInfo && userInfo.is_admin && (
							<li className="side-nav-item">
								<span className="side-nav-link" style={{ display: 'flex', alignItems: 'center' }}>
									<i className="ri-user-3-line"></i>
									<NavDropdown title={t('sidebar.users')} id="UsersDropdown">
										<LinkContainer to="/inactive-users/">
											<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.inactiveUsers')}</NavDropdown.Item>
										</LinkContainer>
										<LinkContainer to="/users-list/">
											<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.usersList')}</NavDropdown.Item>
										</LinkContainer>
									</NavDropdown>
								</span>
							</li>
						)}

						{/* Profile Dropdown */}
						<li className="side-nav-item">
							<span className="side-nav-link" style={{ display: 'flex', alignItems: 'center' }}>
								<i className="ri-profile-line"></i>
								<NavDropdown title={t('sidebar.profile')} id="ProfileDropdown">
									{userInfo && userInfo.is_admin && (
										<LinkContainer to="/businesses/">
											<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.businesses')}</NavDropdown.Item>
										</LinkContainer>
									)}
									<LinkContainer to="/clients/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.clients')}</NavDropdown.Item>
									</LinkContainer>
								</NavDropdown>
							</span>
						</li>

						{/* Items Dropdown */}
						<li className="side-nav-item">
							<span className="side-nav-link" style={{ display: 'flex', alignItems: 'center' }}>
								<i className="ri-box-3-line"></i>
								<NavDropdown title={t('sidebar.items')} id="ItemsDropdown">
									<LinkContainer to="/items/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.items')}</NavDropdown.Item>
									</LinkContainer>
									<LinkContainer to="/items-purchased/">
										<NavDropdown.Item tabIndex={tabIndex}>{t('sidebar.itemsPurchased')}</NavDropdown.Item>
									</LinkContainer>
								</NavDropdown>
							</span>
						</li>
					</ul>
				</div>
			</div>
		</>
	)
}

export default SideBar